import flatpickr from 'flatpickr'
import { format, isValid, parse } from 'date-fns'
import { formatDobInputYYYYMMDD, getCaretPosForDigitIndex } from '@/shared/lib/dob-input'
import { replaceFlatpickrYearInputWithDropdown } from './utils/flatpickr-year-dropdown'

export const FLATPICKR_FOOTER_CLASS = 'flatpickr-footer-buttons'

export interface CreateFlatpickrDateOfBirthOptionsParams {
  defaultDateISO: string | undefined
  onDateChange: (yyyyMmDd: string) => void
  maxDate?: string
}

export function createFlatpickrDateOfBirthOptions(
  params: CreateFlatpickrDateOfBirthOptionsParams
): flatpickr.Options.Options {
  const { defaultDateISO, onDateChange, maxDate = 'today' } = params
  const normalizedMaxDate: string | Date =
    maxDate === 'today'
      ? (() => {
          const d = new Date()
          d.setHours(23, 59, 59, 999)
          return d
        })()
      : /^\d{4}-\d{2}-\d{2}$/.test(maxDate)
        ? (() => {
            const d = parse(maxDate, 'yyyy-MM-dd', new Date())
            d.setHours(23, 59, 59, 999)
            return d
          })()
        : maxDate

  return {
    dateFormat: 'Y/m/d',
    allowInput: true,
    clickOpens: false,
    monthSelectorType: 'dropdown',
    maxDate: normalizedMaxDate,
    defaultDate: defaultDateISO,
    onChange(selectedDates) {
      const date = selectedDates[0]
      onDateChange(date ? format(date, 'yyyy-MM-dd') : '')
    },
    disableMobile: true,
    onReady(_, __, fp) {
      replaceFlatpickrYearInputWithDropdown(fp)

      const inputEl = fp.input
      const handleInput = () => {
        const before = inputEl.value
        const selectionStart = inputEl.selectionStart ?? before.length
        const digitsBeforeCaret = before.slice(0, selectionStart).replace(/[^0-9]/g, '').length
        const formatted = formatDobInputYYYYMMDD(before)

        if (formatted !== before) {
          inputEl.value = formatted
          const nextCaret = getCaretPosForDigitIndex(formatted, digitsBeforeCaret)
          requestAnimationFrame(() => inputEl.setSelectionRange(nextCaret, nextCaret))
        }

        if (/^\d{4}\/\d{2}\/\d{2}$/.test(formatted)) {
          const parsed = parse(formatted, 'yyyy/MM/dd', new Date())
          if (isValid(parsed)) {
            fp.setDate(formatted, false, 'Y/m/d')
            onDateChange(format(parsed, 'yyyy-MM-dd'))
          }
        }
      }

      ;(fp as any).__dobInputHandler = handleInput
      inputEl.addEventListener('input', handleInput)

      const container = (fp as flatpickr.Instance & { calendarContainer: HTMLElement })
        .calendarContainer
      if (!container || container.querySelector(`.${FLATPICKR_FOOTER_CLASS}`)) return

      const footer = document.createElement('div')
      Object.assign(footer, { className: FLATPICKR_FOOTER_CLASS })
      Object.assign(footer.style, {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderTop: '1px solid #e5e7eb',
        marginTop: '4px',
      })

      const footerButtonStyle: Partial<CSSStyleDeclaration> = {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '500',
        color: '#2563eb',
        textTransform: 'uppercase',
        padding: '4px 0',
      }

      const clearBtn = document.createElement('button')
      Object.assign(clearBtn, { type: 'button', textContent: 'Clear' })
      Object.assign(clearBtn.style, footerButtonStyle)
      clearBtn.onmousedown = (event) => {
        event.preventDefault()
      }
      clearBtn.onclick = () => {
        fp.clear(true)
        fp.close()
        requestAnimationFrame(() => fp.input?.focus())
      }

      const todayBtn = document.createElement('button')
      Object.assign(todayBtn, { type: 'button', textContent: 'Today' })
      Object.assign(todayBtn.style, footerButtonStyle)
      todayBtn.onmousedown = (event) => {
        event.preventDefault()
      }
      todayBtn.onclick = () => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        fp.setDate(today, true)
        fp.jumpToDate(today)
        fp.close()
        requestAnimationFrame(() => fp.input?.focus())
      }

      footer.appendChild(clearBtn)
      footer.appendChild(todayBtn)
      container.appendChild(footer)
    },
    onDestroy(_, __, fp) {
      const handler = (fp as any).__dobInputHandler as ((this: HTMLInputElement, ev: Event) => any) | undefined
      if (handler) fp.input?.removeEventListener('input', handler)
    },
  }
}
